/**
 * QuantitySelector Molecule
 *
 * A quantity selector with increment/decrement buttons.
 * Used for selecting ticket quantities in the cart.
 */

'use client';

import useCart from '@/hooks/useCart';
import { Minus, Plus } from 'lucide-react';
import * as React from 'react';
import { twMerge } from 'tailwind-merge';

interface QuantitySelectorProps {
  max?: number;
  defaultValue?: number;
  disabled?: boolean;
  disablePlus?: boolean;
  incrementValue?: number;
  maxCanBePurchased?: number;
  onIncrement?: (count: number) => void;
  onDecrement?: (count: number) => void;
  className?: string;
  remainingTickets?: number;
  minCanBePurchasedInExperience?: number;
}

export const QuantitySelector = ({
  max,
  defaultValue = 0,
  onIncrement,
  onDecrement,
  disabled,
  incrementValue = 1,
  disablePlus,
  className,
  remainingTickets,
  minCanBePurchasedInExperience,
}: QuantitySelectorProps) => {
  const { cart } = useCart();
  const [ticketCount, setTicketCount] = React.useState(defaultValue);
  const [inputValue, setInputValue] = React.useState(String(defaultValue));

  const increaseTicketCount = () => {
    if (!max || ticketCount < max) {
      if (remainingTickets && minCanBePurchasedInExperience && remainingTickets < minCanBePurchasedInExperience) {
        const newCount = ticketCount + remainingTickets;
        setTicketCount(newCount);
        setInputValue(String(newCount));
        if (onIncrement) onIncrement(remainingTickets);
      } else {
        const newCount = ticketCount + incrementValue;
        setTicketCount(newCount);
        setInputValue(String(newCount));
        if (onIncrement) onIncrement(incrementValue);
      }
    }
  };

  const decreaseTicketCount = () => {
    if (ticketCount > 0) {
      const isMinimum = ticketCount === incrementValue;
      const value = isMinimum ? incrementValue : 1;

      const newCount = ticketCount - value;
      setTicketCount(newCount);
      setInputValue(String(newCount));
      if (onDecrement) onDecrement(newCount);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;

    const parsed = parseInt(raw, 10);
    if (isNaN(parsed) || parsed < 0) {
      setInputValue(raw);
      return;
    }

    const clamped = max ? Math.min(parsed, max) : parsed;
    setInputValue(String(clamped));

    if (clamped > ticketCount) {
      const delta = clamped - ticketCount;
      setTicketCount(clamped);
      if (onIncrement) onIncrement(delta);
    } else if (clamped < ticketCount) {
      setTicketCount(clamped);
      if (onDecrement) onDecrement(clamped);
    }
  };

  const handleInputBlur = () => {
    const parsed = parseInt(inputValue, 10);
    if (isNaN(parsed) || parsed < 0) {
      setInputValue(String(ticketCount));
    }
  };

  React.useEffect(() => {
    if (cart.length === 0) {
      setTicketCount(defaultValue);
      setInputValue(String(defaultValue));
    }

    setTicketCount(defaultValue);
    setInputValue(String(defaultValue));
  }, [cart, defaultValue]);

  return (
    <div
      className={twMerge(
        'flex h-10 w-full max-w-[150px] items-center justify-between border border-gray2 px-2 py-1 max-sm:max-w-[110px]',
        className
      )}>
      <div
        className={twMerge(
          'flex h-7 w-7 cursor-pointer items-center justify-center',
          (ticketCount === 0 || disabled) && 'cursor-not-allowed'
        )}
        onClick={disabled ? () => {} : decreaseTicketCount}>
        <Minus className={twMerge('h-5 w-5 text-foreground', (ticketCount === 0 || disabled) && 'text-slate-300')} />
      </div>
      <input
        type='number'
        min={0}
        max={max}
        value={inputValue}
        disabled={disabled}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        className='mx-2 w-full min-w-0 flex-1 select-none border-none bg-transparent text-center font-medium text-foreground outline-none [appearance:textfield] disabled:cursor-not-allowed [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none'
      />
      <div
        className={twMerge(
          'flex h-7 w-7 cursor-pointer items-center justify-center',
          ((max && ticketCount === max) || disablePlus || disabled) && 'cursor-not-allowed'
        )}
        onClick={disabled || disablePlus ? () => {} : increaseTicketCount}>
        <Plus
          className={twMerge(
            'h-5 w-5 text-foreground',
            ((max && ticketCount === max) || disablePlus || disabled) && 'text-slate-300'
          )}
        />
      </div>
    </div>
  );
};

// Alias for backwards compatibility
export const Quantity = QuantitySelector;

export default QuantitySelector;
